import React, {Component} from 'react';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import {app, base} from '../../base';
import '../../styles/css/AdminMain.css';
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import {ErrorMessage, Toast, WarningMessage} from "../utils/HelpfulFunction";

// An admin is a roles/{uid} flag on an ordinary Firebase Auth user — the same
// account the fan app signs in with, because both share one Firebase project.
// Nobody here ever types a password: a new admin gets Firebase's built-in
// password-reset email (sent by Firebase itself, no email service involved)
// and chooses their own; an existing account keeps the password it has.

class AdminManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            adminList: [],
            loading: true,
            modal: false,
            createNewEmail: ""
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.toggle = this.toggle.bind(this);
    }

    componentDidMount() {
        base.bindToState('roles', {
            context: this,
            asArray: true,
            state: 'adminList',
            then(){
                this.setState({
                    loading: false
                })
            },
            onFailure(e){
               console.log(e);
                this.setState({
                    loading: false
                })
            }
        })
    }

    toggle() {
        this.setState({
            modal: !this.state.modal,
            createNewEmail: ""
        });
    }

    handleChange(event){
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        this.setState({[target.name]: value});
    }

    // Firebase Authentication sends this email itself. The link lets the admin
    // set a new password, which — because it is one account — is also their
    // fan-app password.
    async sendResetEmail(email){
        if(!email) return false;
        try {
            await app.auth().sendPasswordResetEmail(email);
            return true;
        } catch (e) {
            console.log(e);
            ErrorMessage.fire({
                title: 'Reset email not sent',
                text: (e && e.message) || 'Firebase could not send the password reset email. Try again in a moment.',
            })
            return false;
        }
    }

    async sendResetForRow(email){
        if(this.state.loading) return;
        this.setState({loading: true});
        const sent = await this.sendResetEmail(email);
        this.setState({loading: false});
        if(sent){
            Toast.fire({
                title: 'Reset email sent to ' + email
            })
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        if(this.state.loading) return;
        const email = (this.state.createNewEmail || "").trim();
        if(!email){
            ErrorMessage.fire({
                title: 'Oh uh!',
                text: 'Need an email',
            })
            return;
        }
        const idToken = await app.auth().currentUser.getIdToken(true);

        const userResponse = {};
        userResponse['email'] = email;
        userResponse['role'] = 'admin';
        let url = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/createOrUpdateAdminUser`;
        if(process.env.NODE_ENV === "development"){
            url = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/createOrUpdateAdminUser`;
        }
        this.setState({loading: true});
        let value = null;
        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization':`Bearer ${idToken}`
                },
                body: JSON.stringify(userResponse),
            });
            value = await res.json();
        } catch (err) {
            ErrorMessage.fire({
                title: 'Oh uh!',
                text: 'Auth or request error; logout and log back in',
            })
            this.setState({loading: false});
            return;
        }
        if(!value || !value.action){
            let errorText = 'Auth or request error; logout and log back in';
            if(value && value.message && value.message !== "Unauthorized" && value.message !== "Forbidden"){
                errorText = value.message;
            }
            ErrorMessage.fire({
                title: 'Oh uh!',
                text: errorText,
            })
            this.setState({loading: false});
            return;
        }
        this.setState({loading: false, modal: false, createNewEmail: ""});
        if(value.action === "created"){
            // Brand-new account: it has an unguessable throwaway password, so
            // the reset email is how the admin gets in at all. If sending
            // failed, sendResetEmail already showed why — don't paint over it.
            const sent = await this.sendResetEmail(email);
            if(sent){
                Toast.fire({
                    title: 'Admin created — password setup email sent to ' + email
                })
            }
        } else if(value.hasPassword === false){
            // A fan who registered with Facebook or Apple. The admin site only
            // signs in with email + password, and a reset email can't add one.
            WarningMessage.fire({
                title: 'Admin access added, but they cannot sign in yet',
                text: email + ' signs in to the fan app with Facebook or Apple, not a password. This admin site only accepts email and password, so they will need an account with a password before they can use it.',
                showCancelButton: false,
                confirmButtonText: 'Ok'
            })
        } else {
            Toast.fire({
                title: email + ' already had an account — admin access added. They sign in with their existing password.'
            })
        }
    }

    async deleteAdmin(uid, email){
        if(!uid){
            return;
        }
        const result = await WarningMessage.fire({
            title: 'Remove admin access?',
            text: 'This takes away their access to this admin site. Their account, and anything they have done in the fan app, stays exactly as it is.',
            confirmButtonText: 'Yes, remove admin access'
        })
        if(!result || !result.value) return;
        const userResponse = {};
        userResponse['uidToDelete'] = uid;
        userResponse['isAdmin'] = true;
        let url = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/deleteUser`;
        if(process.env.NODE_ENV === "development"){
            url = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/deleteUser`;
        }
        const idToken = await app.auth().currentUser.getIdToken(true);
        this.setState({loading: true})
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization':`Bearer ${idToken}`
            },
            body: JSON.stringify(userResponse),
        }).then(res => {
            this.setState({loading:false})
            if(res.status === 500){
                ErrorMessage.fire({
                    title: 'Oh uh!',
                    text: 'Auth or request error; logout and log back in',
                })
                return;
            }
            Promise.resolve(res.json()).then(function (value) {
                if(!value || value.message === "Invalid Permissions") {
                    ErrorMessage.fire({
                        title: 'Oh uh!',
                        text: 'Auth or request error; logout and log back in',
                    })
                    return;
                }
                if(value.stillWhitelisted){
                    // SQWAD staff are let in by the whitelist regardless of roles.
                    WarningMessage.fire({
                        title: 'Admin role removed, but they still have access',
                        text: (email || 'This person') + ' is on the SQWAD whitelist, which grants full access on its own. Remove them from the whitelist to actually revoke it.',
                        showCancelButton: false,
                        confirmButtonText: 'Ok'
                    })
                    return;
                }
                Toast.fire({
                    title: 'Admin access removed'
                })
            })
        }).catch(err => {
            ErrorMessage.fire({
                title: 'Oh uh!',
                text: 'Auth or request error; logout and log back in',
            })
            this.setState({loading: false});
        })
    }

    render() {
        const {loading, adminList=[]} = this.state;
        const currentUser = app.auth().currentUser;
        const myUid = currentUser ? currentUser.uid : null;
        return (
          <div className="admin-wrapper">
            <div className="loading-screen" style={{display: loading ? 'block' : 'none' }}/>
            <SideMenu/>
            <TopMenu/>
            <div className="admin-main-panel">
                <p className="admin-page-eyebrow">Access</p>
                    <p className="admin-header-text">Admin Management</p>
                <p className="admin-subheader-text">Grant or remove access to this admin site. Passwords are never set here — admins choose their own through the reset email.</p>
                <div style={{marginLeft: 20}}>
                    <button className="btn btn-success" onClick={()=>this.setState({modal: true, createNewEmail: ""})}>Add Admin</button>
                </div>
                {adminList.length > 0 &&
                    <div style={{height:700, overflow:"auto"}}>
                        <div className="admin-table-wrap">
                            <table className="table table-striped" style={{color:'black', margin: 20}}>
                                <tbody>
                                <tr>
                                    <th>Email</th>
                                    <th>Password</th>
                                    <th>Access</th>
                                </tr>
                                {
                                    adminList.map(function (item, i) {
                                        return <tr key={i}>
                                            <td style={{fontFamily:'Open Sans' }}>{item.email}</td>
                                            <td style={{fontFamily:'Open Sans'}}>
                                                <button className="btn btn-secondary" disabled={!item.email} onClick={()=>this.sendResetForRow(item.email)}>
                                                    Send reset email
                                                </button>
                                            </td>
                                            <td style={{fontFamily:'Open Sans'}}>
                                                {item.key === myUid ?
                                                    <span className="admin-subheader-text">You</span>
                                                    :
                                                    <button className="btn btn-danger" onClick={()=>this.deleteAdmin(item.key, item.email)}>
                                                        Remove admin
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    }, this)
                                }
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
              </div>
              <Modal isOpen={this.state.modal} toggle={this.toggle} style={{width: '90%'}} id="myModal">
                  <ModalHeader>Add Admin</ModalHeader>
                  <ModalBody>
                      <div className="container-out">
                          <div className="question-box question-form">
                              <form className="pl-3 pr-3" onSubmit={this.handleSubmit} id="create-admin-form">
                                  <div className="form-group">
                                      <label htmlFor="createNewEmail">Email</label>
                                      <input id="createNewEmail" name="createNewEmail" type="email" className="form-control" value={this.state.createNewEmail} onChange={this.handleChange} placeholder="Email"/>
                                  </div>
                                  <p className="admin-subheader-text">
                                      If this email already has a fan account, that account becomes an admin and keeps its password.
                                      Otherwise a new account is created and they get an email to set their password.
                                  </p>
                                  <div style={{padding: 20, textAlign: "center"}}>
                                      <button className="btn btn-success">Submit</button>
                                  </div>
                              </form>
                          </div>
                      </div>
                  </ModalBody>
              </Modal>
         </div>
        );
    }
}

export default AdminManagement
